import React, { useState, useEffect } from 'react';
import { IoCloseSharp } from 'react-icons/io5';
import { appendBankingNodeToPipeline, updateBankingType } from '../../../api/api';
import { Pencil1Icon } from '@radix-ui/react-icons'
import { usePipeline } from '../../../context/PipelineContext';
import { notifyError } from '../../../errors/notifyError';
import './BankingConfigModal.css';

const BankingConfigModal = ({ isOpen, onClose, onConfigured, initialData }) => {
  const [name, setName] = useState('');
  const [bankingType, setBankingType] = useState('daily_tod'); // Default value, can also be 'policy_banking'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { session } = usePipeline();

  // Reset form when modal opens (only on initial open)
  useEffect(() => {
    if (isOpen) {
      setName(initialData?.name || '');
      setBankingType(initialData?.bankingType || 'daily_tod');
      setError('');
    }
  }, [isOpen, initialData?.name, initialData?.bankingType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Validate inputs
      if (!name.trim()) {
        throw new Error('Node name is required');
      }

      // Get the input node name from initialData
      const inputNodeName = initialData?.inputNodeName;
      if (!inputNodeName) {
        throw new Error('No input node connected');
      }

      // Get the position from initialData
      const position = initialData?.position || { x: 0, y: 0 };

      // Check if this is an edit (node already has a banking type) or a new configuration
      const isEdit = initialData?.bankingType && initialData.name === name;

      if (session.pipeline_name !== "Unsaved_Pipeline") {
        try {
          if (isEdit) {
            // Update existing banking node
            await updateBankingType(
              session.pipeline_name,
              name,
              bankingType
            );
            console.log('Banking type updated successfully');
          } else {
            // Create new banking node
            await appendBankingNodeToPipeline(
              session.pipeline_name,
              name,
              bankingType,
              inputNodeName,
              position.x,
              position.y
            );
            console.log('Banking node added successfully');
          }
        } catch (apiError) {
          console.error('Error with banking node API call:', apiError);
          throw apiError;
        }
      }

      // Call the onConfigured callback with the form data
      onConfigured({
        name,
        bankingType
      });
    } catch (error) {
      console.error('Error configuring banking node:', error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="banking-modal-overlay" >
      <div className="banking-modal-content">
        <button onClick={onClose} className="banking-modal-close" aria-label="Close">
          <IoCloseSharp size={20} />
        </button>
        <h2 className="banking-modal-title">{initialData?.bankingType ? 'Edit' : 'Configure'} Banking Node</h2>
        
        {error && (
          <div className="banking-modal-error">
            {error}
          </div>
        )}
        
        {!initialData?.bankingType && (
          <div className="banking-modal-field">
            <label className="banking-modal-label">
              Node Name:
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="banking-modal-input"
                placeholder="Enter node name"
                required
              />
            </label>
          </div>
        )}
        {initialData?.bankingType && (
          <div className="banking-modal-field">
            <label className="banking-modal-label">
              Node Name: <span className="banking-modal-name-display">{name}</span>
            </label>
            <div className="banking-modal-type-display">
              Current Banking Type: <span className="banking-modal-type-value">{initialData.bankingType}</span>
            </div>
          </div>
        )}
        
        <div className="banking-modal-field">
          <label className="banking-modal-label">
            Banking Type:
            <select 
              value={bankingType} 
              onChange={(e) => setBankingType(e.target.value)} 
              className="banking-modal-select"
              required
            >
              <option value="daily_without_tod">Daily without Time of Day</option>
              <option value="monthly_without_tod">Monthly without Time of Day</option>
              <option value="yearly_without_tod">Yearly without Time of Day</option>
              <option value="daily_tod">Daily with Time of Day</option>
              <option value="monthly_tod">Monthly with Time of Day</option>
              <option value="yearly_tod">Yearly with Time of Day</option>
              <option value="policy_banking_MH_2025_corrected">Policy Banking : MH 2025</option>
              <option value="policy_banking_KA_2025">Policy Banking : KA 2025</option>
              <option value="policy_banking_KA_2025_old">Policy Banking : KA 2025 (Old)</option>
              <option value="policy_banking_MH_2025_new">Policy Banking : MH 2025 (New)</option>
              <option value="policy_banking_TN_2025">Policy Banking : TN 2025</option>
              <option value="policy_banking_GH_2025">Policy Banking : GH 2025</option>
            </select>
          </label>
        </div>
        
        {initialData?.bankingType && (
          <>
            {/* Show banking fee for without_tod types */}
            {(['daily_without_tod', 'monthly_without_tod', 'yearly_without_tod'].includes(bankingType)) && (
              <div className="banking-modal-field banking-fee-container">
                <span className="banking-modal-label">Banking Fee:</span>
                <span className="banking-fee-value">10%</span>
                <button 
                  className="banking-edit-button" 
                  onClick={() => notifyError(new Error("This feature will be enabled in the next release."), { toastId: 'banking.featureSoon' })}
                  title="Edit Banking Fee"
                >
                  <Pencil1Icon />
                </button>
              </div>
            )}

            {/* Show tables for tod types */}
            {(['daily_tod', 'monthly_tod', 'yearly_tod'].includes(bankingType)) && (
              <>
                <div className="banking-modal-field banking-table-container">
                  <div className="banking-table-header">
                    <h3>Time Range Settings</h3>
                    <button 
                      className="banking-edit-button" 
                      onClick={() => notifyError(new Error("This feature will be enabled in the next release."), { toastId: 'banking.featureSoon' })}
                      title="Edit Time Range Settings"
                    >
                      <Pencil1Icon />
                    </button>
                  </div>
                  <table className="banking-table">
                    <thead>
                      <tr>
                        <th>Time Range</th>
                        <th>Slot</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>6:00 AM - 10:00 AM</td>
                        <td>peak</td>
                      </tr>
                      <tr>
                        <td>10:00 AM - 6:00 PM</td>
                        <td>offpeak</td>
                      </tr>
                      <tr>
                        <td>6:00 PM - 10:00 PM</td>
                        <td>peak</td>
                      </tr>
                      <tr>
                        <td>10:00 PM - 6:00 AM</td>
                        <td>offpeak</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="banking-modal-field banking-table-container">
                  <div className="banking-table-header">
                    <h3>Inter slot Banking Configurations</h3>
                    <button 
                      className="banking-edit-button" 
                      onClick={() => notifyError(new Error("This feature will be enabled in the next release."), { toastId: 'banking.featureSoon' })}
                      title="Edit Banking Configurations"
                    >
                      <Pencil1Icon />
                    </button>
                  </div>
                  <table className="banking-table">
                    <thead>
                      <tr>
                        <th>Transfer</th>
                        <th>Allowed / Not Allowed</th>
                        <th>Additional charge (if any)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Peak to Peak Transfer</td>
                        <td>Allowed</td>
                        <td>0%</td>
                      </tr>
                      <tr>
                        <td>Peak to Off Peak Transfer</td>
                        <td>Not Allowed</td>
                        <td>N/A</td>
                      </tr>
                      <tr>
                        <td>Off Peak to Off Peak transfer</td>
                        <td>Allowed</td>
                        <td>0%</td>
                      </tr>
                      <tr>
                        <td>Off Peak to Peak transfer</td>
                        <td>Allowed</td>
                        <td>8%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            )}
            
            {/* Show policy banking information */}
            {(bankingType === 'policy_banking_MH_2025_corrected') && (
              <div className="banking-modal-field banking-table-container">
                <div className="banking-table-header">
                  <h3>Policy Banking Configuration</h3>
                  <button 
                    className="banking-edit-button" 
                    onClick={() => notifyError(new Error("This feature will be enabled in the next release."), { toastId: 'banking.featureSoon' })}
                    title="Edit Policy Settings"
                  >
                    <Pencil1Icon />
                  </button>
                </div>
                <div className="banking-policy-info">
                  <p><strong>Current Policy:</strong> Maharashtra 2025</p>
                  <p>This banking type applies the Maharashtra 2025 policy rules for energy banking, including:</p>
                  <ul>
                    <li>Intra-TOD settlement with configurable charges</li>
                    <li>Inter-TOD settlement with configurable charges</li>
                    <li>Time slot definitions for peak and off-peak periods</li>
                    <li>Relationship rules between different time slots</li>
                  </ul>
                </div>
              </div>
            )}
            {(bankingType === 'policy_banking_KA_2025') && (
              <div className="banking-modal-field banking-table-container">
                <div className="banking-table-header">
                  <h3>Policy Banking Configuration</h3>
                  <button 
                    className="banking-edit-button" 
                    onClick={() => notifyError(new Error("This feature will be enabled in the next release."), { toastId: 'banking.featureSoon' })}
                    title="Edit Policy Settings"
                  >
                    <Pencil1Icon />
                  </button>
                </div>
                <div className="banking-policy-info">
                  <p><strong>Current Policy:</strong> Karnataka 2025</p>
                  <p>This banking type applies the  Karnataka 2025 policy rules for energy banking, including:</p>
                  <ul>
                    <li>Intra-TOD settlement with configurable charges</li>
                    <li>Inter-TOD settlement with configurable charges</li>
                    <li>Time slot definitions for peak and off-peak periods</li>
                    <li>Relationship rules between different time slots</li>
                  </ul>
                </div>
              </div>
            )}
            {(bankingType === 'policy_banking_MH_2025_new') && (
              <div className="banking-modal-field banking-table-container">
                <div className="banking-table-header">
                  <h3>Policy Banking Configuration</h3>
                </div>
                <div className="banking-policy-info">
                  <p><strong>Current Policy:</strong> Maharashtra 2025 (New)</p>
                  <p>This banking type applies the Maharashtra 2025 (New) policy rules for energy banking, including:</p>
                  <ul>
                    <li>Intra-TOD settlement with configurable charges</li>
                    <li>Inter-TOD settlement with configurable charges</li>
                    <li>Time slot definitions for peak and off-peak periods</li>
                    <li>Relationship rules between different time slots</li>
                  </ul>
                </div>
              </div>
            )}
            {(bankingType === 'policy_banking_TN_2025') && (
              <div className="banking-modal-field banking-table-container">
                <div className="banking-table-header">
                  <h3>Policy Banking Configuration</h3>
                </div>
                <div className="banking-policy-info">
                  <p><strong>Current Policy:</strong> TN 2025</p>
                  <p>This banking type applies the TN 2025 standard TOD banking rules across three time slots:</p>
                  <ul>
                    <li>Peak: 06:00–10:00 and 18:00–22:00</li>
                    <li>Normal: 05:00–06:00 and 10:00–18:00</li>
                    <li>Off-Peak: 22:00–05:00</li>
                    <li>Same-slot monthly intra-TOD settlement is allowed with an 8% charge</li>
                    <li>Inter-TOD settlement is allowed only from Peak to Normal or Off-Peak, with an 8% charge</li>
                    <li>All other cross-slot transfers are not allowed and the units lapse</li>
                  </ul>
                </div>
              </div>
            )}
            {(bankingType === 'policy_banking_GH_2025') && (
              <div className="banking-modal-field banking-table-container">
                <div className="banking-table-header">
                  <h3>Policy Banking Configuration</h3>
                </div>
                <div className="banking-policy-info">
                  <p><strong>Current Policy:</strong> GH 2025</p>
                  <p>This banking type applies the GH 2025 policy rules for energy banking.</p>
                </div>
              </div>
            )}
          </>
        )}

        <div className="banking-modal-button-container">
          <button 
            type="button" 
            onClick={handleSubmit} 
            className="banking-modal-configure-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : initialData?.bankingType ? 'Update' : 'Configure'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BankingConfigModal;
